import axios, { AxiosError, AxiosInstance, AxiosResponse, AxiosRequestConfig } from "axios";
import { BackendError, SnackbarFunction } from "./utils/interfaces";
import translations from "./translation.json";
import { translate } from "@flow-frontend-npm/common";

const t = (key) => translate(translations, key);

const axiosInstance: AxiosInstance = axios.create({});

let showSnackbar: SnackbarFunction | null = null;

const blockedMessages = new Set<string>();
const SNACKBAR_COOLDOWN = 10000;

let pendingErrorProcessing = false;
const errorQueue: Array<{
    message: string;
    severity: "error" | "warning" | "info" | "success";
}> = [];

export const setSnackbarFunction = (snackbarFunction: SnackbarFunction) => {
    showSnackbar = snackbarFunction;
};

const processNextError = () => {
    if (errorQueue.length === 0) {
        pendingErrorProcessing = false;
        return;
    }

    pendingErrorProcessing = true;
    const { message, severity } = errorQueue.shift()!;

    if (blockedMessages.has(message)) {
        setTimeout(processNextError, 0);
        return;
    }

    blockedMessages.add(message);

    if (showSnackbar) {
        showSnackbar(message, severity);
    }

    setTimeout(() => {
        blockedMessages.delete(message);
    }, SNACKBAR_COOLDOWN);

    setTimeout(processNextError, 100);
};

const queueSnackbar = (
    message: string,
    severity: "error" | "warning" | "info" | "success",
) => {
    errorQueue.push({ message, severity });

    if (!pendingErrorProcessing) {
        processNextError();
    }
};

const handleError = (error: AxiosError): Promise<never> => {
    const status = error.response?.status;
    const config = error.config as AxiosRequestConfig;
    const backendError = error.response?.data as BackendError;

    if ( (error.response?.status === 404 && config?.skipErrorHandlerFor404) ||
        (error.response?.status === 500 && config?.skipErrorHandlerFor500) ) {
        return Promise.reject(error);
    }

    if (!error.response) {
        const message = t("networkError");
        queueSnackbar(message, "error");
        return Promise.reject(error);
    }

    if (backendError.translatedVisualizerMessage) {
        return Promise.reject(error);
    }

    let errorMessage = "";
    if (!status) {
        errorMessage = t("notReachable");
    } else if (status >= 500) {
        errorMessage = backendError?.message || t("500error");
    } else if (status >= 400) {
        errorMessage = backendError?.message || t("400error");
    } else {
        errorMessage =
            backendError?.message || error.message || t("otherError");
    }

    queueSnackbar(errorMessage, "error");

    return Promise.reject(error);
};

axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => handleError(error),
);

export default axiosInstance;
